<script setup>
/**
 * GviaView
 * Main container for payment collection (גבייה) feature
 * Unified flow: select client → select invoices → choose payment method
 */
import { computed, onUnmounted } from 'vue'
import GviaSelectClient from './GviaSelectClient.vue'
import GviaPaymentDetails from './GviaPaymentDetails.vue'
import { useGvia } from '@/core/composables/useGvia'

const { currentStep, reset } = useGvia()

// Step indicator data
const steps = [
  { id: 'select', label: 'בחירת לקוח', icon: 'user' },
  { id: 'payments', label: 'בחירת חשבוניות', icon: 'list' },
]

const currentStepIndex = computed(() => {
  return steps.findIndex((s) => s.id === currentStep.value)
})

// Cleanup on unmount
onUnmounted(() => {
  reset()
})
</script>

<template>
  <div class="gvia-view">
    <!-- Header -->
    <div class="view-header">
      <h1 class="view-title">גבייה</h1>
    </div>

    <!-- Step Indicator -->
    <div class="step-indicator">
      <div
        v-for="(step, index) in steps"
        :key="step.id"
        class="step"
        :class="{
          active: currentStep === step.id,
          completed: index < currentStepIndex,
        }"
      >
        <div class="step-number">
          <template v-if="index < currentStepIndex">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </template>
          <template v-else>
            {{ index + 1 }}
          </template>
        </div>
        <span class="step-label">{{ step.label }}</span>
        <div v-if="index < steps.length - 1" class="step-connector"></div>
      </div>
    </div>

    <!-- Content -->
    <div class="view-content">
      <Transition name="slide" mode="out-in">
        <GviaSelectClient v-if="currentStep === 'select'" />
        <GviaPaymentDetails v-else-if="currentStep === 'payments'" />
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.gvia-view {
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--header-height));
  padding: var(--spacing-md);
  max-width: 800px;
  margin: 0 auto;
  overflow: hidden;
}

/* Header */
.view-header {
  text-align: center;
  margin-bottom: var(--spacing-md);
}

.view-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

/* Step Indicator */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--spacing-md);
}

.step {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--bg-tertiary);
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  transition: all var(--transition-fast);
}

.step.active .step-number {
  background: var(--color-primary);
  color: white;
}

.step.completed .step-number {
  background: var(--color-success);
  color: white;
}

.step-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  transition: color var(--transition-fast);
}

.step.active .step-label {
  color: var(--text-primary);
}

.step.completed .step-label {
  color: var(--color-success);
}

.step-connector {
  width: 40px;
  height: 2px;
  background: var(--border-color);
  margin: 0 var(--spacing-xs);
}

.step.completed + .step .step-connector,
.step.completed .step-connector {
  background: var(--color-success);
}

/* Content */
.view-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.view-content > * {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

/* Responsive */
@media (max-width: 600px) {
  .gvia-view {
    padding: var(--spacing-sm);
  }

  .step-label {
    display: none;
  }

  .step.active .step-label {
    display: inline;
  }

  .step-connector {
    width: 24px;
  }
}
</style>
